import { useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { cardLast4 } from '@heybesafe/shared';
import { useSafe } from '../context/SafeContext';
import { HbsButton } from '../components/hbs/Button';
import { Icon } from '../components/hbs/Icon';
import { CardRow } from '../components/CardRow';

/**
 * The My Payment Cards list — a personal wallet of stored payment cards, a peer
 * to My Safe and My Contacts. Lists the user's personal `card`-type items
 * (cards are personal-only in v1 — never shared into a family). Search matches
 * the card name, the cardholder name, and the last-4 digits.
 */
export default function CardsPage(): JSX.Element {
  const { items, itemsLoaded, loadingStep } = useSafe();
  const navigate = useNavigate();
  const { id: selectedId } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get('q') ?? '';

  function setQuery(value: string): void {
    const next = new URLSearchParams(searchParams);
    if (value) next.set('q', value);
    else next.delete('q');
    setSearchParams(next, { replace: true });
  }

  const cards = useMemo(
    () => items.filter((i) => i.type === 'card' && !i.orgId),
    [items],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const matches = q
      ? cards.filter((i) => {
          const haystack = [
            i.name,
            i.cardholderName ?? '',
            cardLast4(i.cardNumber ?? ''),
          ]
            .join(' ')
            .toLowerCase();
          return haystack.includes(q);
        })
      : cards;
    return [...matches].sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }),
    );
  }, [cards, query]);

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="sticky top-0 bg-hbs-bg z-10 border-b border-hbs-border px-5 pt-[18px] pb-3">
        <div className="flex items-start gap-2">
          <div className="flex-1 min-w-0">
            <h1 className="font-display font-semibold text-[24px] tracking-hbs-tight text-hbs-text">
              My Payment Cards
            </h1>
            <p className="text-[12px] text-hbs-text-mute mt-0.5">
              {cards.length} {cards.length === 1 ? 'card' : 'cards'} · sorted by name
            </p>
          </div>
          <HbsButton
            id="new-card-button"
            title="Add a new payment card"
            onClick={() => navigate('/cards/new')}
            leadingIcon={<Icon name="plus" size={14} />}
          >
            New
          </HbsButton>
        </div>
        <div className="mt-2 flex items-center gap-2 h-9 px-2.5 bg-hbs-surface border border-hbs-border rounded-hbs-sm">
          <Icon name="search" size={15} className="text-hbs-text-mute" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search payment cards"
            aria-label="Search payment cards"
            className="flex-1 min-w-0 bg-transparent text-[13.5px] text-hbs-text placeholder:text-hbs-text-mute focus:outline-none"
          />
        </div>
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto p-2">
        {/* loadingStep alone was not enough: unlock() flips status to 'unlocked'
            and only sets loadingStep several round-trips later, so this pane
            was skipped during exactly the window it exists to cover. Key it on
            itemsLoaded and keep loadingStep for its more specific wording. */}
        {!itemsLoaded && items.length === 0 ? (
          <div role="status" aria-live="polite" className="flex items-center justify-center h-full text-hbs-text-mute text-[13px]">
            {loadingStep ?? 'Loading payment cards…'}
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-hbs-text-mute gap-2 p-8 text-center">
            <Icon name="card" size={40} className="text-hbs-text-mute" />
            <p className="text-[13px]">
              {query ? 'No matches' : 'No payment cards yet'}
            </p>
            {!query && (
              <HbsButton
                id="add-first-card-button"
                title="Add a new payment card"
                variant="ghost"
                onClick={() => navigate('/cards/new')}
              >
                Add your first payment card
              </HbsButton>
            )}
          </div>
        ) : (
          <ul className="flex flex-col gap-px">
            {filtered.map((item) => (
              <li key={item.id}>
                <CardRow item={item} selected={selectedId === item.id} />
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
